<template>
  <div v-if="uploadedImage" class="relative">
    <button type="button" class="absolute -right-1 -top-1" @click="removeImage">
      <Icon
        icon="mdi-close"
        class="w-8 h-8 text-white bg-red-600 border border-red-600 rounded-full hover:animate-pulse" />
    </button>
    <img :src="uploadedImage" />
  </div>
  <div
    v-else
    class="relative block w-full p-12 text-center border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-theme-500 focus:ring-offset-2"
    @dragover.prevent="drag = true"
    @dragleave.prevent="drag = false"
    @drop.prevent="onDrop"
    @click="triggerFileInput"
    :class="{ 'border-theme-600': drag }">
    <input type="file" accept="image/*" class="hidden" ref="fileInput" @change="onFileChange" />
    <Icon icon="iconoir:add-media-image" class="w-12 h-12 mx-auto"> </Icon>
    <span class="block mt-2 text-sm font-medium"> {{ $t('imageUpload') }}</span>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';
import { Icon } from '@iconify/vue';

const { t: $t } = useI18n({ useScope: 'global' });
const toastStore = useToastStore();

const props = defineProps<{
  image?: string;
}>();

const uploadedImage = ref<string | null>();

onMounted(() => {
  if (props.image) uploadedImage.value = props.image;
});
const drag = ref(false);

function onDrop(event: DragEvent) {
  drag.value = false;
  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    onUploadImage(files[0]);
  }
}

const emit = defineEmits(['image-changed']);

function onFileChange(event: Event) {
  const target = event.target as HTMLInputElement;
  const files = target.files;
  if (files && files.length > 0) {
    onUploadImage(files[0]);
  }
}

const MAX_FILE_SIZE = 1 * 1024 * 1024;

function onUploadImage(file: File) {
  if (!file.type.startsWith('image/')) {
    toastStore.error({ text: `${$t('messages.errors.fileTypeNotAllowed')}` });
    return;
  }
  if (file.size > MAX_FILE_SIZE) {
    toastStore.error({ text: `${$t('messages.errors.largeFile')}` });
    return;
  }
  uploadedImage.value = URL.createObjectURL(file);
  emit('image-changed', uploadedImage.value);
}

const fileInput = ref<HTMLInputElement | null>(null);

function triggerFileInput(): void {
  if (fileInput.value) fileInput.value.click();
}

function removeImage() {
  if (uploadedImage.value) {
    URL.revokeObjectURL(uploadedImage.value);
    uploadedImage.value = null;
    emit('image-changed', null);
  }
}
</script>
